import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, NgModule, OnInit } from '@angular/core';
import { RouterModule } from '@angular/router';
import { Cms, PAGE_TYPES } from '@impact/data';
import { TranslateModule } from '@ngx-translate/core';

import { StyleBreakpoints } from '../../models/style-breakpoints';
import { RichTextModule } from '../../rich-text/rich-text.module';
import { SectionHeaderModule } from '../../section-header/section-header.module';
import { SpotsUiModule } from '../../spots/spots-ui.module';
import { IImageBreakpoints } from '../../utils/responsive-image.component';
import { UtilsModule } from '../../utils/utils.module';

@Component({
    selector: 'app-article-page',
    template: `
        <div appScrollProgress></div>
        <main [ngSwitch]="data?.displayPageTopBanner" id="scrollProgressContainer">
            <!-- Page top banner -->
            <section *ngSwitchCase="true" class="article-page__top-banner">
                <div class="article-page__intro--fixed" [ngClass]="{'small-banner' : data?.displaySmallTopBanner}">
                    <app-responsive-image *ngIf="data?.articleIntroImage"
                        [image]="data?.articleIntroImage"
                        [breakpoints]="bannerLargeImageBreakpoints"
                        [lazyLoad]="false"
                        class="article-page__intro-image">
                    </app-responsive-image>

                    <div class="article-page__intro-overlay" [ngStyle]="{ 'opacity': topBannerOverlayOpacity }"></div>
                    <div class="article-page__intro-content">
                        <div class="article__info">
                            <div class="article__info-tags" *ngIf="data?.articleCategoryTag">
                                <div *ngFor="let tag of data?.articleCategoryTag" class="article__info-tag">
                                    {{tag?.tagDisplayName}}
                                </div>
                            </div>
                        </div>
                        <app-section-header class="article-page__header"
                            [headerTextPrimary]="data?.articleTitle"
                            [headerSize]="1"
                            [isCentered]="true"
                            [introText]="data?.articleSubTitle">

                            <div class="article-page__publish-info">
                                <div class="article-page__author">
                                    <div class="article-page__author-image" *ngIf="data?.articleAuthor?.authorImage?.src">
                                        <img [src]="data?.articleAuthor?.authorImage?.src | resolveMediaUrl: { format:'webp', width: 90, height:90 }"
                                            [alt]="data?.articleAuthor?.authorImage?.alt" loading="lazy" />
                                    </div>
                                    <div>{{data?.articleAuthor?.authorName}}</div>
                                </div>
                                <div class="article__publish-info-seperator">&middot;</div>
                                <time class="article__info-date" [attr.datetime]="data?.articleCreatedDate">{{data?.articleCreatedDate | date: 'dd. MMM yyyy' }}</time>
                            </div>
                        </app-section-header>
                    </div>
                </div>
            </section>

            <!-- Normal -->
            <section *ngSwitchCase="false" class="page-content layout-wrapper article-page__page-content">
                <div class="article-page__intro">
                    <div class="article__info">
                        <div class="article__info-tags" *ngIf="data?.articleCategoryTag">
                            <div *ngFor="let tag of data?.articleCategoryTag" class="article__info-tag">
                                {{tag?.tagDisplayName}}
                            </div>
                        </div>
                    </div>
                    <app-section-header class="article-page__header"
                        [headerTextPrimary]="data?.articleTitle"
                        [headerSize]="1"
                        [isCentered]="true"
                        [introText]="data?.articleSubTitle">

                        <div class="article-page__publish-info">
                            <div class="article-page__author">
                                <div class="article-page__author-image" *ngIf="data?.articleAuthor?.authorImage?.src">
                                    <img [src]="data?.articleAuthor?.authorImage?.src | resolveMediaUrl: { format: 'webp', width: 40, height: 40, rmode: 'crop' }"
                                        [alt]="data?.articleAuthor?.authorImage?.alt" />
                                </div>
                                <div class="">{{data?.articleAuthor?.authorName}}</div>
                            </div>
                            <div class="article__publish-info-seperator">&middot;</div>
                            <time class="article__info-date" [attr.datetime]="data?.articleCreatedDate">{{data?.articleCreatedDate | date: 'dd. MMM yyyy' }}</time>
                        </div>
                    </app-section-header>
                </div>
                <app-responsive-image *ngIf="data?.articleIntroImage"
                    [image]="data?.articleIntroImage"
                    [breakpoints]="bannerImageBreakpoints"
                    [lazyLoad]="false"
                    class="article-page__intro-image">
                </app-responsive-image>
            </section>

            <!-- Article Page grid -->
            <section class="page-grid-content page-grid-content--narrow" *ngIf="data?.mainGrid">
                <app-spot-grid [grid]="data?.mainGrid"></app-spot-grid>
            </section>
        </main>

        <section class="page-grid-content" *ngIf="data?.pageBottomGrid">
            <app-spot-grid [grid]="data?.pageBottomGrid"></app-spot-grid>
        </section>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ArticlePageComponent implements OnInit {
    public static ref = PAGE_TYPES.ARTICLE_PAGE;

    @Input() data?: Cms.ArticlePage;

    topBannerOverlayOpacity: number;
    bannerImageBreakpoints: IImageBreakpoints[];
    bannerLargeImageBreakpoints: IImageBreakpoints[];

    constructor() {
        this.bannerImageBreakpoints = [
            {
                breakpoint: StyleBreakpoints.Implicit,
                width: 360
            },
            {
                breakpoint: StyleBreakpoints.Xsmall,
                width: 460
            },
            {
                breakpoint: StyleBreakpoints.Small,
                width: 768
            },
            {
                breakpoint: StyleBreakpoints.Medium,
                width: 1024
            },
            {
                breakpoint: StyleBreakpoints.Large,
                width: 1200,
            },
            {
                breakpoint: StyleBreakpoints.Xlarge,
                width: 1530,
            }
        ];

        this.bannerLargeImageBreakpoints = [
            {
                breakpoint: StyleBreakpoints.Implicit,
                width: 360
            },
            {
                breakpoint: StyleBreakpoints.Xsmall,
                width: 460
            },
            {
                breakpoint: StyleBreakpoints.Small,
                width: 768
            },
            {
                breakpoint: StyleBreakpoints.Medium,
                width: 1024
            },
            {
                breakpoint: StyleBreakpoints.Large,
                width: 1200,
            },
            {
                breakpoint: StyleBreakpoints.Xlarge,
                width: 2000,
            }
        ];
    }

    ngOnInit() {
        this.topBannerOverlayOpacity = this.data?.topBannerOverlayOpacity ? Number(this.data?.topBannerOverlayOpacity) / 100 : 0;
    }
}
@NgModule({
    declarations: [ArticlePageComponent],
    imports: [
        TranslateModule.forChild(),
        SpotsUiModule,
        CommonModule,
        RouterModule,
        RichTextModule,
        UtilsModule,
        SpotsUiModule,
        SectionHeaderModule
    ]
})
class ArticlePageModule { }

export const LazyComponent = ArticlePageComponent;
